import * as Yup from 'yup'

import {
  AdditionalUserFormData,
  ChangePasswordParams,
  CheckSsoFormData,
  CreateUserFormData,
  ForgotPasswordFormData,
  ResetPasswordParams,
  SetInitialPasswordParams,
  SignInFormData,
  VerifyProfileAccessTokenParams,
} from '@/types/user.type'
import {
  CreateMessageParams,
  InitMessageParams,
  MessageSender,
  MessageStatus,
  FunctionMode,
  SelectableModel,
  PrimerFormParams,
} from '@/types/message.type'
import { ConversationStatus, UpdateConversationParams } from '@/types/conversation.type'
import { UpdateMessageEvaluationParams } from '@/types/message_evaluation.type'
import { FilterMetadata, SearchFilter } from '@/types/filter.type'
import { CreateInvitationParams } from '@/types/invitation.type'
import { USER_ROLES } from '@/constants/roles_and_permissions'
import {
  CallSummarizerParams,
  ConvertCyFyParams,
  OwnershipDataParams,
  PrimerParams,
  PulseCheckParams,
  ScreenerParams,
  ScreenerQuestionParams,
  StructuredDataParams,
  TrackOwnershipParams,
  CatalystParams,
  TranscriptSummaryParams,
  UpdateScreenerParams,
  UploadDocumentsParams,
  CompanyPageParams,
} from '@/types/workflow.type'
import { TrendSearchFormParams } from '@/types/trend.type'
import {
  MeetingStatus,
  MeetingType,
  BookMeetingParams,
  UpdatePublicMeetingParams,
  UpdateInvalidMeetingParams,
} from '@/types/meeting_information.type'
import {
  ZOOM_REGISTRATION_LINK_MESSAGE,
  isZoomRegistrationLink,
} from '@/lib/meetingRegistrationLink'

export const SignInFormSchema: Yup.ObjectSchema<SignInFormData> = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is Required'),
  password: Yup.string().required('Password is Required'),
})

export const SignUpFormSchema: Yup.ObjectSchema<CreateUserFormData> = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(15, 'Password is too short - should be 15 chars minimum.')
    .matches(/(?=.*[0-9])/, 'Password must contain a number.')
    .required('Password is required'),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Password confirmation is required'),
  invitation_token: Yup.string(),
  country_code: Yup.string().required('Country code is required'),
  phone_number: Yup.string()
    .min(7, 'Phone number must be at least 7 digits')
    .max(15, 'Phone number must be less than 15 digits')
    .required('Phone number is required'),
})

export const CheckSsoFormSchema: Yup.ObjectSchema<CheckSsoFormData> = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is Required'),
})

export const AdditionalInfoSchema: Yup.ObjectSchema<AdditionalUserFormData> = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  country_code: Yup.string().required('Country code is required'),
  phone_number: Yup.string()
    .min(7, 'Phone number must be at least 7 digits')
    .max(15, 'Phone number must be less than 15 digits')
    .required('Phone number is required'),
})

export const ForgotPasswordSchema: Yup.ObjectSchema<ForgotPasswordFormData> = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
})

export const ResetPasswordSchema: Yup.ObjectSchema<ResetPasswordParams> = Yup.object().shape({
  password: Yup.string()
    .min(15, 'Password is too short - should be 15 chars minimum.')
    .matches(/(?=.*[0-9])/, 'Password must contain a number.')
    .required('Required'),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Password confirmation is required'),
  reset_password_token: Yup.string().required('Reset password token is required'),
})

export const SetInitialPasswordSchema: Yup.ObjectSchema<SetInitialPasswordParams> =
  Yup.object().shape({
    password: Yup.string()
      .min(15, 'Password is too short - should be 15 chars minimum.')
      .matches(/(?=.*[0-9])/, 'Password must contain a number.')
      .required('Required'),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
      .required('Password confirmation is required'),
  })

export const InitMessageSchema: Yup.ObjectSchema<InitMessageParams> = Yup.object().shape({
  selected_model: Yup.mixed<SelectableModel>().required(),
  content: Yup.string().required('Required'),
  sender: Yup.mixed<MessageSender>().required(),
  created_at: Yup.string().required('Required'),
  status: Yup.mixed<MessageStatus>().required(),
  filters: Yup.mixed<SearchFilter>(),
  onyx_chat_session_id: Yup.string(),
  function_mode: Yup.mixed<FunctionMode>(),
  optional_references: Yup.array(),
})

export const CreateMessageSchema: Yup.ObjectSchema<CreateMessageParams> = Yup.object().shape({
  conversation_id: Yup.string().required('Required'),
  parent_message_id: Yup.string(),
  selected_model: Yup.mixed<SelectableModel>().required(),
  content: Yup.string().required('Required'),
  sender: Yup.mixed<MessageSender>().required(),
  input_tokens: Yup.number(),
  output_tokens: Yup.number(),
  start_time: Yup.number(),
  time_to_first_byte: Yup.number(),
  completion_time: Yup.number(),
  status: Yup.mixed<MessageStatus>(),
  is_execute_search: Yup.boolean(),
  filters: Yup.mixed<SearchFilter>(),
  filter_metadata: Yup.mixed<FilterMetadata>(),
  function_content: Yup.string(),
  function_mode: Yup.mixed<FunctionMode>(),
  optional_references: Yup.array(),
  workspace_model: Yup.string(),
  dpc_mode: Yup.string(),
  onyx_chat_message_id: Yup.string().nullable(),
})

export const UpdateConversationSchema: Yup.ObjectSchema<UpdateConversationParams> =
  Yup.object().shape({
    id: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
    is_starred: Yup.boolean(),
    filters: Yup.mixed<SearchFilter>(),
    status: Yup.mixed<ConversationStatus>(),
    start_model: Yup.mixed<SelectableModel>(),
    onyx_chat_session_id: Yup.string(),
    vault_selections: Yup.object().shape({
      uploads: Yup.array().of(Yup.string().required()),
      storage_documents: Yup.array().of(Yup.string().required()),
      storage_containers: Yup.array().of(Yup.string().required()),
    }),
    remove_vault_selections: Yup.object().shape({
      uploads: Yup.array().of(Yup.string().required()),
      storage_documents: Yup.array().of(Yup.string().required()),
      storage_containers: Yup.array().of(Yup.string().required()),
    }),
  })

export const UpdateMessageEvaluationSchema: Yup.ObjectSchema<UpdateMessageEvaluationParams> =
  Yup.object().shape({
    id: Yup.string().required('Required'),
    feedback: Yup.string().required('Required'),
  })

export const CreateInvitationSchema: Yup.ObjectSchema<CreateInvitationParams> = Yup.object().shape({
  emails: Yup.array()
    .of(Yup.string().email('Invalid email format').required('Email is required'))
    .required('Emails field is required'),
  role_name: Yup.mixed<USER_ROLES>().required('Role field is required'),
})

export const ConvertCyFyWorkflowSchema: Yup.ObjectSchema<ConvertCyFyParams> = Yup.object().shape({})

export const UploadDocumentsWorkflowSchema: Yup.ObjectSchema<UploadDocumentsParams> =
  Yup.object().shape({
    prompt: Yup.string().required('Required'),
  })

export const PrimerWorkflowSchema: Yup.ObjectSchema<PrimerParams> = Yup.object().shape({
  stock: Yup.object().shape({
    ticker: Yup.string().required('Required'),
    id: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
    labelText: Yup.string().required('Required'),
  }),
  country: Yup.object().shape({
    id: Yup.string().required('Required'),
    labelText: Yup.string().required('Required'),
  }),
})

export const PulseCheckWorkflowSchema: Yup.ObjectSchema<PulseCheckParams> = Yup.object().shape({
  theme: Yup.string().required('Required'),
  category: Yup.string().required('Required'),
  keywords_of_mention: Yup.string().when('theme', {
    is: 'mention',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.default(undefined),
  }),
  start_date: Yup.date().required('Required'),
  end_date: Yup.date().required('Required'),
  industry: Yup.object()
    .shape({
      labelText: Yup.string().required('Required'),
      id: Yup.string().required('Required'),
    })
    .when('category', {
      is: 'industry',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.default(undefined),
    }),
  country: Yup.object()
    .shape({
      labelText: Yup.string().required('Required'),
      id: Yup.string().required('Required'),
    })
    .when('category', {
      is: 'industry',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.default(undefined),
    }),
  stocks: Yup.array(
    Yup.object().shape({
      id: Yup.string().required('Required'),
      ticker: Yup.string().required('Required'),
    })
  ).when('category', {
    is: 'custom',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.default(undefined),
  }),
})

export const TranscriptSummarySchema: Yup.ObjectSchema<TranscriptSummaryParams> =
  Yup.object().shape({
    custom_guide: Yup.string(),
  })

export const OwnershipDataSchema: Yup.ObjectSchema<OwnershipDataParams> = Yup.object().shape({
  stock: Yup.object().shape({
    id: Yup.string().required('Required'),
  }),
})

export const TrackOwnershipSchema: Yup.ObjectSchema<TrackOwnershipParams> = Yup.object().shape({
  conversation_id: Yup.string().required(),
  message_id: Yup.string(),
  start_time: Yup.number(),
  filters: Yup.object()
    .shape({
      stocks: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            ticker: Yup.string().required(),
            name: Yup.string().required(),
            currency: Yup.string().required(),
          })
        )
        .required(),
    })
    .required(),
})

export const CatalystSchema: Yup.ObjectSchema<CatalystParams> = Yup.object().shape({
  stock_ids: Yup.array()
    .of(Yup.string().required())
    .min(1, 'A stock is required')
    .max(1, 'Only one stock is allowed')
    .required(),
  conversation_id: Yup.string().required('Required'),
})

export const CompanyPageSchema: Yup.ObjectSchema<CompanyPageParams> = Yup.object().shape({
  conversation_id: Yup.string().required('Required'),
  parent_message_id: Yup.string(),
  lang: Yup.string().default('en'),
  filters: Yup.object()
    .shape({
      stocks: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            ticker: Yup.string().required(),
            name: Yup.string().required(),
            exchange: Yup.string(),
            country: Yup.string(),
          })
        )
        .required(),
    })
    .required(),
})

export const ScreenerSchema: Yup.ObjectSchema<ScreenerParams> = Yup.object().shape({
  conversation_id: Yup.string().required('Required'),
  category: Yup.string().required('Required'),
  filters: Yup.object().shape({
    industry: Yup.array(Yup.string().required('Each industry must be a non-empty string')).when(
      '$category', // context에서 'category'를 가져오기
      {
        is: 'industry',
        then: (schema) => schema.required('Industry is required when category is "industry"'),
        otherwise: (schema) => schema.default(undefined),
      }
    ),
    country: Yup.array(Yup.string().required('Each country must be a non-empty string')).when(
      '$category', // context에서 'category'를 가져오기
      {
        is: 'industry',
        then: (schema) => schema.required('Country is required when category is "industry"'),
        otherwise: (schema) => schema.default(undefined),
      }
    ),
    stocks: Yup.array(
      Yup.object().shape({
        id: Yup.string().required('Required'),
        ticker: Yup.string().required('Required'),
      })
    ).when('$category', {
      is: 'custom',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.default(undefined),
    }),
    metric: Yup.array().of(
      Yup.object().shape({
        name: Yup.string(),
        operator: Yup.string(),
        value: Yup.number(),
        desc: Yup.string(),
        unit: Yup.string(),
      })
    ),
  }),
})

export const ScreenerQuestionSchema: Yup.ObjectSchema<ScreenerQuestionParams> = Yup.object({
  table_id: Yup.string().required('table_id is required'),
  metadata: Yup.object({
    inputs: Yup.object({
      topic: Yup.string().required('Topic is required'),
      document_type: Yup.array()
        .of(Yup.string().required()) // Ensure no undefined values in the array
        .required('Document Type is required')
        .min(1, 'Document Type must have at least one item'),
    }).required('inputs is required'),
  }).required('metadata is required'),
})

export const UpdateScreenerSchema: Yup.ObjectSchema<UpdateScreenerParams> = Yup.object().shape({
  table_id: Yup.string().required('Required'),
  category: Yup.string().required('Required'),
  filters: Yup.object().shape({
    industry: Yup.array(Yup.string().required('Each industry must be a non-empty string')).when(
      '$category', // context에서 'category'를 가져오기
      {
        is: 'industry',
        then: (schema) => schema.required('Industry is required when category is "industry"'),
        otherwise: (schema) => schema.default(undefined),
      }
    ),
    country: Yup.array(Yup.string().required('Each country must be a non-empty string')).when(
      '$category', // context에서 'category'를 가져오기
      {
        is: 'industry',
        then: (schema) => schema.required('Country is required when category is "industry"'),
        otherwise: (schema) => schema.default(undefined),
      }
    ),
    stocks: Yup.array(
      Yup.object().shape({
        id: Yup.string().required('Required'),
        ticker: Yup.string().required('Required'),
      })
    ).when('$category', {
      is: 'custom',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.default(undefined),
    }),
    metric: Yup.array().of(
      Yup.object().shape({
        name: Yup.string(),
        operator: Yup.string(),
        value: Yup.number(),
        desc: Yup.string(),
        unit: Yup.string(),
      })
    ),
  }),
})

export const TrendSearchFormSchema: Yup.ObjectSchema<TrendSearchFormParams> = Yup.object().shape({
  keyword: Yup.string().required('Keyword is required'),
  country: Yup.string().nullable(),
})

export const CallSummarizerSchema: Yup.ObjectSchema<CallSummarizerParams> = Yup.object().shape({
  conversation_id: Yup.string().required(),
  start_time: Yup.number(),
  filters: Yup.object()
    .shape({
      stocks: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            ticker: Yup.string().required(),
          })
        )
        .required(),
      file_key: Yup.string().required('Required'),
    })
    .required(),
})

export const StructuredDataSchema: Yup.ObjectSchema<StructuredDataParams> = Yup.object().shape({
  conversation_id: Yup.string().required(),
  message_id: Yup.string(),
  start_time: Yup.number(),
  filters: Yup.object()
    .shape({
      stock_id_list: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            ticker: Yup.string().required(),
          })
        )
        .min(1)
        .required(),
      metric_list: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string().required(),
            desc: Yup.string().required(),
          })
        )
        .min(1)
        .required(),
    })
    .required(),
})

export const VerifyProfileAccessTokenSchema: Yup.ObjectSchema<VerifyProfileAccessTokenParams> =
  Yup.object().shape({
    token: Yup.string().required('Token is required'),
  })

// Expert Call compliance fields (PRD #3260) — max length shared with the form
// input maxLength and the server-side validation.
export const MAX_EXPERT_FIELD_LENGTH = 30

type BookMeetingSchemaOptions = {
  requireMeetingType?: boolean
  requireExpertFields?: boolean
}

// Expert Call fields are required only for Expert Network Calls, and only for
// users who can tag meetings (requireForExpertCall). Any other meeting type
// keeps them optional so they can stay hidden/empty.
const expertFieldSchema = (label: string, requireForExpertCall: boolean) => {
  const base = Yup.string()
    .trim()
    .max(MAX_EXPERT_FIELD_LENGTH, `${label} must be ${MAX_EXPERT_FIELD_LENGTH} characters or fewer`)
  if (!requireForExpertCall) return base.optional()
  return base.when('meeting_type', {
    is: MeetingType.ExpertNetworkCall,
    then: (schema) => schema.required(`Please enter the ${label.toLowerCase()}`),
    otherwise: (schema) => schema.optional(),
  })
}

// Blocks Zoom registration pages only. Other registration links are allowed with a
// warning in MeetingConnectionFields, and the agent's post-booking check decides (ENG-262).
export const MeetingLinkSchema = () =>
  Yup.string().test(
    'is-zoom-registration-link',
    ZOOM_REGISTRATION_LINK_MESSAGE,
    (value) => !isZoomRegistrationLink(value)
  )

export const BookMeetingSchema = (
  options: BookMeetingSchemaOptions = {}
): Yup.ObjectSchema<BookMeetingParams> =>
  Yup.object().shape({
    meeting_id: Yup.string().when(['platform', 'link'], {
      is: (platform: string, link: string | undefined) =>
        (platform === 'zoom' || !platform || platform.trim() === '') &&
        (!link || link.trim() === ''),
      then: (schema) => schema.required('Please enter a meeting ID'),
      otherwise: (schema) => schema.notRequired(),
    }),
    platform: Yup.string().required('Please select a platform'),
    meeting_name: Yup.string().required('Please enter a title for your meeting'),
    link: MeetingLinkSchema().when('platform', {
      is: (platform: string) => platform !== 'zoom',
      then: (schema) =>
        schema
          .required('A meeting link is required for non-Zoom platforms.')
          .url(
            'Please enter a valid URL format (e.g. https://zoom.us/j/1234567890?pwd=1234567890)'
          ),
      otherwise: (schema) =>
        schema
          .notRequired()
          .test(
            'is-valid-url-if-provided',
            'Please enter a valid URL format (e.g. https://zoom.us/j/1234567890?pwd=1234567890)',
            (value) => {
              if (!value || value.trim() === '') return true
              try {
                new URL(value)
                return true
              } catch {
                return false
              }
            }
          ),
    }),
    password_or_passcode: Yup.string(),
    meeting_date: Yup.date().required('Please select a meeting date'),
    start_time: Yup.string().required('Please select a start time for your meeting'),
    end_time: Yup.string().required('Please select an end time for your meeting'),
    duration: Yup.number(),
    participant: Yup.string(),
    speaker_language: Yup.string().required('Please select the language spoken in the meeting'),
    summary_language: Yup.string().required('Please select a language for the summary'),
    timezone: Yup.string().required('Please select your timezone'),
    status: Yup.mixed<MeetingStatus>().required('Status is required'),
    content: Yup.string().required('Please provide meeting details'),
    custom_guide: Yup.string(),
    participant_fullname: Yup.string(),
    // Blank → undefined ("use the default resolution"). Yup's .email() alone
    // passes addresses the server's @IsEmail rejects (abs@ss, a@b.c, a@b.co1),
    // so require a 2+ letter TLD to match validator.js.
    user_email: Yup.string()
      .transform((value) => {
        if (typeof value !== 'string') return value
        const trimmed = value.trim()
        return trimmed === '' ? undefined : trimmed
      })
      .email('Please enter a valid email address')
      .matches(/^\S+@\S+\.[A-Za-z]{2,}$/, 'Please enter a valid email address'),
    meeting_type: options.requireMeetingType
      ? Yup.mixed<MeetingType>()
          .oneOf(Object.values(MeetingType), 'Please select a meeting type')
          .required('Please select a meeting type')
      : Yup.mixed<MeetingType>().oneOf(Object.values(MeetingType)).optional(),
    expert_name: expertFieldSchema('Expert name', options.requireExpertFields ?? false),
    employer: expertFieldSchema('Employer', options.requireExpertFields ?? false),
    analyst_name: expertFieldSchema('Analyst name', options.requireExpertFields ?? false),
    // Shape only (LIN-306). Eligibility — same organization, not the booker —
    // is decided server-side, so there is nothing to validate here.
    recipient_user_ids: Yup.array().of(Yup.string().required()).optional(),
  })

export const UpdateMeetingSchema: Yup.ObjectSchema<UpdatePublicMeetingParams> = Yup.object().shape({
  id: Yup.string().required('ID is required'),
  token: Yup.string().required('Token is required'),
  link: MeetingLinkSchema().url(
    'Please enter a valid URL format (e.g. https://zoom.us/j/1234567890?pwd=1234567890)'
  ),
  meeting_id: Yup.string().when('link', {
    is: (link: string | undefined) => !link || link.trim() === '',
    then: (schema) => schema.required('Please enter a meeting ID'),
    otherwise: (schema) => schema.notRequired(),
  }),
  password_or_passcode: Yup.string(),
})

export const TfsPrimerWorkflowSchema: Yup.ObjectSchema<PrimerFormParams> = Yup.object().shape({
  stock: Yup.object().shape({
    ticker: Yup.string().required('Required'),
    id: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
    labelText: Yup.string().required('Required'),
  }),
  country: Yup.object().shape({
    id: Yup.string().required('Required'),
    labelText: Yup.string().required('Required'),
  }),
})

export const UpdateInvalidMeetingSchema: Yup.ObjectSchema<UpdateInvalidMeetingParams> =
  Yup.object().shape({
    id: Yup.string().required('ID is required'),
    link: MeetingLinkSchema().url(
      'Please enter a valid URL format (e.g. https://zoom.us/j/1234567890?pwd=1234567890)'
    ),
    password_or_passcode: Yup.string(),
    meeting_id: Yup.string().when('link', {
      is: (link: string | undefined) => !link || link.trim() === '',
      then: (schema) => schema.required('Please enter a meeting ID'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

export const ChangePasswordSchema: Yup.ObjectSchema<ChangePasswordParams> = Yup.object().shape({
  current_password: Yup.string().required('Please enter your current password'),
  new_password: Yup.string()
    .min(15, 'Password must be at least 15 characters')
    .max(128, 'Password is too long')
    .matches(/(?=.*[0-9])/, 'Password must contain at least one digit')
    .notOneOf([Yup.ref('current_password')], 'New password must be different from current password')
    .required('Please enter a new password'),
  new_password_confirmation: Yup.string()
    .oneOf([Yup.ref('new_password'), undefined], 'Passwords do not match')
    .required('Please confirm your new password'),
})
